import { lazy, Suspense, useEffect, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import ErrorBoundary from './components/ErrorBoundary'
import Layout from './components/Layout'
import LoginPage from './pages/LoginPage'
import OfficePresenceHeartbeat from './components/OfficePresenceHeartbeat'
import TelegramBotNotifications from './components/TelegramBotNotifications'
import ProtectedRoute from './components/ProtectedRoute'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { PortalUiLanguageProvider } from './contexts/PortalUiLanguageContext'
import Loading from './components/Loading'
import { employeeHomeHref, isEmployeeHardNavHomePath } from './utils/employeeDeskPath'
import { SYSTEM_AI_ADMIN_BASE, SYSTEM_AI_EMPLOYER_BASE } from './utils/systemAiRoutes'

const EmployeeHomePage = lazy(() => import('./pages/EmployeeHomePage'))
const EmployeeSeoDashboardPage = lazy(() => import('./pages/EmployeeSeoDashboardPage'))
const AdminTelegramPortalBotPage = lazy(() => import('./pages/AdminTelegramPortalBotPage'))
const TelegramBotInboxPage = lazy(() => import('./pages/TelegramBotInboxPage'))

function RootRedirect() {
  const { isAdmin, loading, user } = useAuth()
  if (loading) {
    return <Loading fullScreen text="Загрузка..." />
  }
  if (window.location.hostname.toLowerCase() === 'office.alex-digital.io') {
    return <Navigate to="/office" replace />
  }
  if (isAdmin) {
    return <ServerHtmlRedirect href="/home" />
  }
  const href = employeeHomeHref(user)
  if (isEmployeeHardNavHomePath(href)) {
    return <ServerHtmlRedirect href={href} />
  }
  return <Navigate to={href} replace />
}

function EmployeeCanonicalHome() {
  const { loading } = useAuth()
  if (loading) {
    return <Loading fullScreen text="Загрузка..." />
  }
  return <EmployeeHomePage />
}

function EmployeeDeskRoute() {
  return <Navigate to="/employee/home" replace />
}

function LegacyEmployeeTaskRedirect() {
  const { taskId } = useParams<{ taskId?: string }>()
  const to = taskId
    ? '/employee/tasks/task/' + encodeURIComponent(taskId)
    : '/employee/tasks'
  return <Navigate to={to} replace />
}

function ServerHtmlRedirect({ href }: { href: string }) {
  useEffect(() => {
    // Force a document request so FastAPI/Jinja remains the source of truth.
    window.location.replace(href)
  }, [href])
  return <Loading fullScreen text="Открываем полную HTML-версию…" />
}

function AdminHtmlHubRedirect() {
  const location = useLocation()
  return <ServerHtmlRedirect href={`/admin${location.search}${location.hash}`} />
}

function AdminCanonicalHome() {
  const location = useLocation()
  return <ServerHtmlRedirect href={`/home${location.search}${location.hash}`} />
}

function AdminClientsHtmlRedirect() {
  const location = useLocation()
  return <ServerHtmlRedirect href={`/admin/clients${location.search}${location.hash}`} />
}

function AdminClientHtmlRedirect() {
  const { clientId } = useParams<{ clientId?: string }>()
  const location = useLocation()
  if (!clientId) return <Navigate to="/admin/clients" replace />
  return (
    <ServerHtmlRedirect
      href={`/admin/client/${encodeURIComponent(clientId)}${location.search}${location.hash}`}
    />
  )
}

const OfficePage = lazy(() => import('./pages/OfficePage'))
const AdminLandingPage = lazy(() => import('./pages/AdminLandingPage'))
const AdminBlogPage = lazy(() => import('./pages/AdminBlogPage'))
const AdminCasesPage = lazy(() => import('./pages/AdminCasesPage'))
const AdminPortfolioPage = lazy(() => import('./pages/AdminPortfolioPage'))
const AdminAiVisibilityPage = lazy(() => import('./pages/AdminAiVisibilityPage'))
const AdminWebsiteMonitoringPage = lazy(() => import('./pages/AdminWebsiteMonitoringPage'))
const AdminMailAlertPage = lazy(() => import('./pages/AdminMailAlertPage'))
const AdminMonitoringTgPage = lazy(() => import('./pages/AdminMonitoringTgPage'))
const AdminMonitoringFaqPage = lazy(() => import('./pages/AdminMonitoringFaqPage'))
const AdminMonitoringScenariosPage = lazy(() => import('./pages/AdminMonitoringScenariosPage'))
const AdminMonitoringTriggersPage = lazy(() => import('./pages/AdminMonitoringTriggersPage'))
const SystemAiAnalyticsPage = lazy(() => import('./pages/SystemAiAnalyticsPage'))
const SystemAiClientDailyPage = lazy(() => import('./pages/SystemAiClientDailyPage'))
const TaskBoardV2Page = lazy(() => import('./pages/TaskBoardV2Page'))
const TaskBoardV2ChecklistsPage = lazy(() => import('./pages/TaskBoardV2ChecklistsPage'))
const CreativePortalRedirect = lazy(() => import('./meta-ai/CreativePortalRedirect'))
const CreativeCalendarPage = lazy(() => import('./pages/CreativeCalendarPage'))
const FixZonePage = lazy(() => import('./pages/FixZonePage'))
const CreativeCalendarCampaignPage = lazy(() => import('./pages/CreativeCalendarCampaignPage'))
const CreativeCalendarMetaOverviewPage = lazy(() => import('./pages/CreativeCalendarMetaOverviewPage'))
const CreativeBriefSchedulerPage = lazy(() => import('./pages/CreativeBriefSchedulerPage'))
const AdminEmployeeEffectivenessPage = lazy(() => import('./pages/AdminEmployeeEffectivenessPage'))
const AdminAppClientsPage = lazy(() => import('./pages/AdminAppClientsPage'))
const AdminAppalPage = lazy(() => import('./pages/AdminAppalPage'))
const AdminAdsAppSettingsPage = lazy(() => import('./pages/AdminAdsAppSettingsPage'))
const EmployeeRewardsPage = lazy(() => import('./pages/EmployeeRewardsPage'))
const AdminEmployeesPage = lazy(() => import('./pages/AdminEmployeesPage'))
const AdminEmployeeWorkspacePage = lazy(() => import('./pages/AdminEmployeeWorkspacePage'))
const AdminClientWorkspacePage = lazy(() => import('./pages/AdminClientWorkspacePage'))
const AdminInvoicesPage = lazy(() => import('./pages/AdminInvoicesPage'))
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage'))
const AdminPortalUsersPage = lazy(() => import('./pages/AdminPortalUsersPage'))
const AdminSubscriptionsPage = lazy(() => import('./pages/AdminSubscriptionsPage'))
const AdminGoogleOperationsPage = lazy(() => import('./pages/AdminGoogleOperationsPage'))
const AdminFacebookApiPage = lazy(() => import('./pages/AdminFacebookApiPage'))
const AdminGoogleApiPage = lazy(() => import('./pages/AdminGoogleApiPage'))
const AdminFacebookAlertsPage = lazy(() => import('./pages/AdminFacebookAlertsPage'))
const AdminFacebookAdsPage = lazy(() => import('./pages/AdminFacebookAdsPage'))
const AdminMetaAdsManagerPage = lazy(() => import('./pages/AdminMetaAdsManagerPage'))
const MetaAiPage = lazy(() => import('./pages/MetaAiPage'))
const GoogleAiPage = lazy(() => import('./pages/GoogleAiPage'))
const AdminCrmApiPage = lazy(() => import('./pages/AdminCrmApiPage'))
const AdminRedzoneSettingsPage = lazy(() => import('./pages/AdminRedzoneSettingsPage'))
const AdminScoreLearningPage = lazy(() => import('./pages/AdminScoreLearningPage'))
const AdminAiManagerPage = lazy(() => import('./pages/AdminAiManagerPage'))
const EmployeeSettingsPage = lazy(() => import('./pages/EmployeeSettingsPage'))
const EmployeeClientsPage = lazy(() => import('./pages/EmployeeClientsPage'))
const TeamHubPage = lazy(() => import('./pages/TeamHubPage'))

/** Админ с /employer/system-ai-analytics → канонический URL в админке. */
function EmployerFleetAiAdminRedirect({ children }: { children: ReactNode }) {
  const { isAdmin } = useAuth()
  const loc = useLocation()
  const legacyBase = '/employer/system-ai-analytics'
  const sourceBase = loc.pathname.startsWith(legacyBase) ? legacyBase : SYSTEM_AI_EMPLOYER_BASE
  const suffix = loc.pathname.startsWith(sourceBase) ? loc.pathname.slice(sourceBase.length) : ''
  const base = isAdmin ? SYSTEM_AI_ADMIN_BASE : '/employee/system-ai-analytics'
  const to = `${base}${suffix}${loc.search}`
  if (loc.pathname === to.split('?')[0]) return <>{children}</>
  return <Navigate to={to} replace />
}

function TeamWorkspaceLegacyRedirect() {
  const { isAdmin } = useAuth()
  const location = useLocation()
  const base = isAdmin ? '/admin/slack' : '/employee/slack'
  return <Navigate to={`${base}${location.search}${location.hash}`} replace />
}

function EmployeeClientHtmlRedirect() {
  const { user } = useAuth()
  const { clientId } = useParams<{ clientId?: string }>()
  const location = useLocation()
  if (!clientId) return <Navigate to="/employee/clients" replace />
  const positions = [user?.normalized_position, ...(user?.normalized_positions || [])]
  if (positions.includes('DataAnalyst') || positions.includes('ProjectLaunchSpecialist')) return <AdminClientWorkspacePage />
  return (
    <ServerHtmlRedirect
      href={`/employee/client/${encodeURIComponent(clientId)}${location.search}${location.hash}`}
    />
  )
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <PortalUiLanguageProvider>
            <AuthProvider>
            <OfficePresenceHeartbeat />
            <TelegramBotNotifications />
            <Suspense fallback={<Loading fullScreen text="Загрузка раздела…" />}>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/office" element={<ProtectedRoute><OfficePage /></ProtectedRoute>} />
              <Route path="/home" element={<AdminCanonicalHome />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<RootRedirect />} />
                <Route path="meta-ai/*" element={<ProtectedRoute requirePermission="meta_ai.view"><MetaAiPage /></ProtectedRoute>} />
                <Route path="google-ai/*" element={<ProtectedRoute requirePermission="google.metrics.view"><GoogleAiPage /></ProtectedRoute>} />
                <Route path="employee/home" element={<EmployeeCanonicalHome />} />
                <Route path="employee/settings" element={<EmployeeSettingsPage />} />
                <Route
                  path="employee/clients"
                  element={
                    <ProtectedRoute requirePermission="client.view_all">
                      <EmployeeClientsPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="employee/telegram-bot" element={<TelegramBotInboxPage />} />
                <Route path="workspace" element={<TeamWorkspaceLegacyRedirect />} />
                <Route path="employee/workspace" element={<TeamWorkspaceLegacyRedirect />} />
                <Route
                  path="employee/slack/*"
                  element={
                    <ProtectedRoute>
                      <TeamHubPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employee/invoices"
                  element={
                    <ProtectedRoute requirePermission="invoice.view">
                      <AdminInvoicesPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="assistant/settings" element={<Navigate to="/employee/settings" replace />} />
                <Route path="employee/desk/:roleSlug/:employeeId" element={<EmployeeDeskRoute />} />
                <Route
                  path="seo"
                  element={
                    <ProtectedRoute requirePermission="seo.manage">
                      <EmployeeSeoDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="seo/table"
                  element={
                    <ProtectedRoute requirePermission="seo.manage">
                      <EmployeeSeoDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="seo/react-preview"
                  element={
                    <ProtectedRoute requirePermission="seo.manage">
                      <EmployeeSeoDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="seo/table/react-preview"
                  element={
                    <ProtectedRoute requirePermission="seo.manage">
                      <EmployeeSeoDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="seo_seo"
                  element={
                    <ProtectedRoute requirePermission="seo.manage">
                      <Navigate to="/seo" replace />
                    </ProtectedRoute>
                  }
                />




                <Route path="admin/seomap-v2/*" element={<ServerHtmlRedirect href="/admin/seomap-v2" />} />
                <Route path="employee/board" element={<Navigate to="/employee/tasks" replace />} />
                <Route path="employee/rewards" element={<ServerHtmlRedirect href="/employee/home" />} />
                <Route
                  path="employee/client/:clientId"
                  element={
                    <ProtectedRoute requirePermission="client.view">
                      <EmployeeClientHtmlRedirect />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminHtmlHubRedirect />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/clients"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminClientsHtmlRedirect />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/employees"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminEmployeesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/employees/react-preview"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminEmployeesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/employees/:employeeId"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminEmployeeWorkspacePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/employees/:employeeId/react-preview"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminEmployeeWorkspacePage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/invoices"
                  element={
                    <ProtectedRoute requirePermission="invoice.view">
                      <AdminInvoicesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/invoices/react-preview"
                  element={
                    <ProtectedRoute requirePermission="invoice.view">
                      <AdminInvoicesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/portal-users"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminPortalUsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/subscriptions"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminSubscriptionsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/google-ads-calendar"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminGoogleOperationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/google-ads-calendar/react-preview"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminGoogleOperationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="google_api"
                  element={
                    <ProtectedRoute requirePermission="google.metrics.view">
                      <AdminGoogleApiPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="facebook_api"
                  element={
                    <ProtectedRoute requirePermission="facebook.metrics.view">
                      <AdminFacebookApiPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="facebook_api/alerts"
                  element={
                    <ProtectedRoute requirePermission="facebook.metrics.view">
                      <AdminFacebookAlertsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="facebook_api/ads"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminFacebookAdsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="facebook_api/manage"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminMetaAdsManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="crm_api"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminCrmApiPage />
                    </ProtectedRoute>
                  }
                />                <Route
                  path="admin/google-ads-auto"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminGoogleOperationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/google-ads-auto/react-preview"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminGoogleOperationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/auto-tasks"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminGoogleOperationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/auto-tasks/react-preview"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminGoogleOperationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/task-automation"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminGoogleOperationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/task-automation/react-preview"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminGoogleOperationsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="settings/react-preview"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/settings/redzone"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminRedzoneSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/score-learning"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminScoreLearningPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/ai-manager/*"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminAiManagerPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/client/:clientId"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminClientHtmlRedirect />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/app-clients"
                  element={<ProtectedRoute requireAdmin={true}><AdminAppClientsPage /></ProtectedRoute>}
                />
                <Route
                  path="admin/appal"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminAppalPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/ads-app"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminAdsAppSettingsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/employee-effectiveness/*"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminEmployeeEffectivenessPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/gamify"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <EmployeeRewardsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/landing"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminLandingPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/blog/*"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminBlogPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/case_approve/*"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminCasesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/slack/*"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <TeamHubPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/cases/*"
                  element={
                    <ProtectedRoute requirePermission="cases.manage">
                      <AdminCasesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employee/cases/*"
                  element={
                    <ProtectedRoute requirePermission="cases.manage">
                      <AdminCasesPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/portfolio/*"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminPortfolioPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/ai-visibility"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminAiVisibilityPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/fix-zone"
                  element={<ProtectedRoute requirePermission="analytics.fix_zone"><FixZonePage /></ProtectedRoute>}
                />
                <Route
                  path="employee/fix-zone"
                  element={<ProtectedRoute requirePermission="analytics.fix_zone"><FixZonePage /></ProtectedRoute>}
                />
                <Route
                  path="admin/system-ai-analytics/:clientId/daily"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <SystemAiClientDailyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/system-ai-analytics"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <SystemAiAnalyticsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employer/system-ai-analytics/:clientId/daily"
                  element={
                    <ProtectedRoute requireFleetAiAnalytics={true}>
                      <EmployerFleetAiAdminRedirect>
                        <SystemAiClientDailyPage />
                      </EmployerFleetAiAdminRedirect>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employer/system-ai-analytics"
                  element={
                    <ProtectedRoute requireFleetAiAnalytics={true}>
                      <EmployerFleetAiAdminRedirect>
                        <SystemAiAnalyticsPage />
                      </EmployerFleetAiAdminRedirect>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employee/system-ai-analytics/:clientId/daily"
                  element={
                    <ProtectedRoute requireFleetAiAnalytics={true}>
                      <SystemAiClientDailyPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employee/system-ai-analytics"
                  element={
                    <ProtectedRoute requireFleetAiAnalytics={true}>
                      <SystemAiAnalyticsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/task-2-0/task/:taskId"
                  element={
                    <ProtectedRoute requireTaskBoardV2={true}>
                      <TaskBoardV2Page />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/task-2-0/checklists"
                  element={
                    <ProtectedRoute requireTaskBoardV2={true}>
                      <TaskBoardV2ChecklistsPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/task-2-0"
                  element={
                    <ProtectedRoute requireTaskBoardV2={true}>
                      <TaskBoardV2Page />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="assistant/tasks/task/:taskId"
                  element={<LegacyEmployeeTaskRedirect />}
                />
                <Route
                  path="assistant/tasks/*"
                  element={<LegacyEmployeeTaskRedirect />}
                />
                <Route
                  path="employee/tasks/task/:taskId"
                  element={
                    <ProtectedRoute>
                      <TaskBoardV2Page />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employee/tasks/*"
                  element={
                    <ProtectedRoute>
                      <TaskBoardV2Page />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employee/task-2-0/task/:taskId"
                  element={<LegacyEmployeeTaskRedirect />}
                />
                <Route
                  path="employee/task-2-0"
                  element={<LegacyEmployeeTaskRedirect />}
                />
                <Route
                  path="employee/task-2-0/*"
                  element={<LegacyEmployeeTaskRedirect />}
                />
                <Route
                  path="admin/creative-calendar/meta-overview"
                  element={
                    <ProtectedRoute requireCreativePortal={true}>
                      <CreativePortalRedirect><CreativeCalendarMetaOverviewPage /></CreativePortalRedirect>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/creative-calendar/briefs"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <CreativePortalRedirect><CreativeBriefSchedulerPage /></CreativePortalRedirect>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/creative-calendar/campaign/:id"
                  element={
                    <ProtectedRoute requireCreativePortal={true}>
                      <CreativePortalRedirect><CreativeCalendarCampaignPage /></CreativePortalRedirect>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/creative-calendar"
                  element={
                    <ProtectedRoute requireCreativePortal={true}>
                      <CreativePortalRedirect><CreativeCalendarPage /></CreativePortalRedirect>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employee/creative-calendar/meta-overview"
                  element={
                    <ProtectedRoute requireCreativePortal={true}>
                      <CreativeCalendarMetaOverviewPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employee/creative-calendar/campaign/:id"
                  element={
                    <ProtectedRoute requireCreativePortal={true}>
                      <CreativeCalendarCampaignPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="employee/creative-calendar"
                  element={
                    <ProtectedRoute requireCreativePortal={true}>
                      <CreativeCalendarPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/telegram-portal-bot"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminTelegramPortalBotPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/website-monitoring"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminWebsiteMonitoringPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/mail-alert"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminMailAlertPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/monitoring_tg"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminMonitoringTgPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/monitoring_tg/faq"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminMonitoringFaqPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/monitoring_tg/scenarios"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminMonitoringScenariosPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="admin/monitoring_tg/triggers"
                  element={
                    <ProtectedRoute requireAdmin={true}>
                      <AdminMonitoringTriggersPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="employee/monitoring_tg" element={<AdminMonitoringTgPage />} />
                <Route path="employee/monitoring_tg/faq" element={<AdminMonitoringFaqPage />} />
                <Route path="employee/monitoring_tg/scenarios" element={<AdminMonitoringScenariosPage />} />
                <Route path="employee/monitoring_tg/triggers" element={<AdminMonitoringTriggersPage />} />
                {/* Добавляем другие роуты по мере миграции */}
              </Route>
            </Routes>
            </Suspense>
            </AuthProvider>
          </PortalUiLanguageProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}

export default App
